import forge from "node-forge";
import { v4 } from "uuid";
import { arrayBufferToBase64, base64ToArrayBuffer, decodeBase64, strToArrayBuffer, toArrayBuffer } from "./common";

// let key = decodeURIComponent(process.env.REACT_APP_ENCRYPTION_KEY!);
let key = decodeBase64(process.env.REACT_APP_ENCRYPTION_KEY!);

export function generateUUID(length = 36) {
  const uuidV = v4().slice(0, length);
  return uuidV;
}

export function encrypt(text: string): string {
  let iv = generateUUID(16);
  let cipher = forge.cipher.createCipher("AES-CBC", key);
  cipher.start({ iv: iv });
  const utf8Bytes = forge.util.encodeUtf8(text);
  cipher.update(forge.util.createBuffer(utf8Bytes));
  cipher.finish();
  let encrypted = cipher.output;
  let d = forge.util.encode64(encrypted.data);
  return `${d}|${iv}`;
}

export function decrypt(encryptedText: string): any {
  const splittedText = encryptedText?.split("|");
  const [encData, iv] = [splittedText?.[0], splittedText?.[1]];
  let decipher = forge.cipher.createDecipher("AES-CBC", key);
  decipher.start({ iv: iv });
  decipher.update(forge.util.createBuffer(forge.util.decode64(encData)));
  let result = decipher.finish();
  if (result) {
    return forge.util.decodeUtf8(decipher.output.getBytes());
  } else {
    throw new Error("Could not decrypt");
  }
}

export class RSACipher {
  encrypt(text: string, publicKey: string): string {
    try {
      const encrypted = forge.pki
        .publicKeyFromPem(publicKey)
        .encrypt(text, "RSA-OAEP", {
          md: forge.md.sha256.create(),
          mgf1: {
            md: forge.md.sha256.create(),
          },
        });

      return forge.util.encode64(encrypted);
    } catch (e) {
      console.error("RSA encryption failed:", e);
      throw new Error("RSA encryption failed");
    }
  }
}

async function aesEncrypt(data: string) {
  const key = crypto.getRandomValues(new Uint8Array(32)); // 256-bit key
  const iv = crypto.getRandomValues(new Uint8Array(12));  // 12 bytes for GCM

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const encodedData = strToArrayBuffer(data);

  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
    },
    cryptoKey,
    encodedData
  );

  // Split ciphertext + authTag
  const encryptedArray = new Uint8Array(encryptedBuffer);
  const authTag = encryptedArray.slice(-16); // last 16 bytes
  const cipherText = encryptedArray.slice(0, -16);

  return {
    key,
    iv,
    encryptedData: arrayBufferToBase64(cipherText),
    authTag: arrayBufferToBase64(authTag),
  };
};

export async function importPublicKey(pem: string): Promise<CryptoKey> {
  // ✅ Fix escaped newlines
  pem = pem.replace(/\\n/g, "\n");

  // ✅ Remove headers/footers
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s/g, ""); // removes all whitespace/newlines

  // ✅ Decode base64 → binary
  const binaryDer = base64ToArrayBuffer(base64);

  // ✅ Import key
  return crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );
};

export async function rsaEncrypt(data: Uint8Array, publicKey: CryptoKey) {
  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    toArrayBuffer(data)
  );

  return arrayBufferToBase64(encrypted);
};

export async function hybridEncrypt(
  plainText: string,
  publicKeyPem: string
) {
  // Step 1: AES encryption
  const { key, iv, encryptedData, authTag } =
    await aesEncrypt(plainText);

  // Step 2: Import RSA key
  const publicKey = await importPublicKey(publicKeyPem);

  // Step 3: Encrypt AES key and IV
  const encryptedKey = await rsaEncrypt(key, publicKey);
  const encryptedIv = await rsaEncrypt(iv, publicKey);

  return {
    data: `${encryptedData}:${encryptedKey}:${encryptedIv}:${authTag}`
  };
};